import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2 } from "lucide-react";

interface DiscountCode {
  code: string;
  type: "percentage" | "fixed" | "per_team_volume";
  value: number;
  valid_from?: string;
  valid_until?: string;
  usage_limit?: number;
  is_active: boolean;
}

interface CircuitCode {
  code: string;
  discount_per_team: number;
  description: string;
  overrides_multi_team: boolean;
  is_active: boolean;
}

interface MultiTeamDiscount {
  enabled: boolean;
  min_teams: number;
  discount_per_team: number;
  description: string;
}

interface ProcessingFee {
  enabled: boolean;
  percentage: number;
  applies_to: string;
  description: string;
}

interface DiscountsTabProps {
  discountCodes: DiscountCode[];
  multiTeamDiscount?: MultiTeamDiscount;
  circuitCodes?: CircuitCode[];
  processingFee?: ProcessingFee;
  onUpdate: (data: {
    discountCodes: DiscountCode[];
    multiTeamDiscount?: MultiTeamDiscount;
    circuitCodes?: CircuitCode[];
    processingFee?: ProcessingFee;
  }) => void;
}

export default function DiscountsTab({ 
  discountCodes, 
  multiTeamDiscount = { enabled: false, min_teams: 2, discount_per_team: 2500, description: "Save $25 per team when registering 2+ teams" },
  circuitCodes = [],
  processingFee = { enabled: false, percentage: 3.0, applies_to: "credit_card", description: "Credit card processing fee" },
  onUpdate 
}: DiscountsTabProps) {
  const addDiscount = () => {
    onUpdate({
      discountCodes: [
        ...discountCodes,
        { code: "", type: "percentage", value: 0, is_active: true },
      ],
      multiTeamDiscount,
      circuitCodes,
      processingFee
    });
  };

  const updateDiscount = (index: number, field: keyof DiscountCode, value: any) => {
    const updated = [...discountCodes];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate({ discountCodes: updated, multiTeamDiscount, circuitCodes, processingFee });
  };

  const removeDiscount = (index: number) => {
    onUpdate({ 
      discountCodes: discountCodes.filter((_, i) => i !== index),
      multiTeamDiscount,
      circuitCodes,
      processingFee
    });
  };

  const updateMultiTeamDiscount = (field: keyof MultiTeamDiscount, value: any) => {
    onUpdate({
      discountCodes,
      multiTeamDiscount: { ...multiTeamDiscount, [field]: value },
      circuitCodes,
      processingFee
    });
  };

  const addCircuitCode = () => {
    onUpdate({
      discountCodes,
      multiTeamDiscount,
      circuitCodes: [
        ...circuitCodes,
        { code: "", discount_per_team: 10000, description: "Circuit discount", overrides_multi_team: true, is_active: true }
      ],
      processingFee
    });
  };

  const updateCircuitCode = (index: number, field: keyof CircuitCode, value: any) => {
    const updated = [...circuitCodes];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate({ discountCodes, multiTeamDiscount, circuitCodes: updated, processingFee });
  };

  const removeCircuitCode = (index: number) => {
    onUpdate({
      discountCodes,
      multiTeamDiscount,
      circuitCodes: circuitCodes.filter((_, i) => i !== index),
      processingFee
    });
  };

  const updateProcessingFee = (field: keyof ProcessingFee, value: any) => {
    onUpdate({
      discountCodes,
      multiTeamDiscount,
      circuitCodes,
      processingFee: { ...processingFee, [field]: value }
    });
  };

  return (
    <div className="space-y-6">
      {/* Multi-Team Automatic Discount */}
      <Card>
        <CardHeader>
          <CardTitle>Multi-Team Automatic Discount</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="multi-team-enabled">Enable Multi-Team Discount</Label>
              <p className="text-sm text-muted-foreground">Automatically applied when registering multiple teams</p>
            </div>
            <Switch
              id="multi-team-enabled"
              checked={multiTeamDiscount.enabled}
              onCheckedChange={(checked) => updateMultiTeamDiscount("enabled", checked)}
            />
          </div>

          {multiTeamDiscount.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min-teams">Minimum Teams Required</Label>
                  <Input
                    id="min-teams"
                    type="number"
                    min="2"
                    value={multiTeamDiscount.min_teams}
                    onChange={(e) => updateMultiTeamDiscount("min_teams", parseInt(e.target.value) || 2)}
                  />
                </div>
                <div>
                  <Label htmlFor="discount-per-team">Discount Per Team ($)</Label>
                  <Input
                    id="discount-per-team"
                    type="number"
                    min="0"
                    value={multiTeamDiscount.discount_per_team / 100}
                    onChange={(e) => updateMultiTeamDiscount("discount_per_team", Math.round((parseFloat(e.target.value) || 0) * 100))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    ${(multiTeamDiscount.discount_per_team / 100).toFixed(2)} off per team
                  </p>
                </div>
              </div>
              <div>
                <Label htmlFor="multi-team-description">Description</Label>
                <Input
                  id="multi-team-description"
                  value={multiTeamDiscount.description}
                  onChange={(e) => updateMultiTeamDiscount("description", e.target.value)}
                  placeholder="e.g., Save $25 per team when registering 2+ teams"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Circuit Team Coupon Codes */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Circuit Team Coupon Codes</CardTitle>
              <p className="text-sm text-muted-foreground">Special codes that give $ off per team</p>
            </div>
            <Button onClick={addCircuitCode} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Circuit Code
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {circuitCodes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No circuit codes configured. Click "Add Circuit Code" to create special per-team discount codes.
            </p>
          ) : (
            circuitCodes.map((code, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label>Code</Label>
                        <Input
                          placeholder="e.g., RLCIRCUIT2025"
                          value={code.code}
                          onChange={(e) => updateCircuitCode(index, "code", e.target.value.toUpperCase())}
                        />
                      </div>
                      <div>
                        <Label>Discount Per Team ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={code.discount_per_team / 100}
                          onChange={(e) => updateCircuitCode(index, "discount_per_team", Math.round((parseFloat(e.target.value) || 0) * 100))}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          ${(code.discount_per_team / 100).toFixed(2)} off per team (applied to ALL teams)
                        </p>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={code.description}
                          onChange={(e) => updateCircuitCode(index, "description", e.target.value)}
                          placeholder="e.g., RL Circuit - $100 off per team"
                        />
                      </div>
                      <div className="flex items-center justify-between space-x-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`overrides-${index}`}
                            checked={code.overrides_multi_team}
                            onCheckedChange={(checked) => updateCircuitCode(index, "overrides_multi_team", checked)}
                          />
                          <Label htmlFor={`overrides-${index}`}>Overrides Multi-Team Discount</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Label htmlFor={`active-circuit-${index}`}>Active</Label>
                          <Switch
                            id={`active-circuit-${index}`}
                            checked={code.is_active}
                            onCheckedChange={(checked) => updateCircuitCode(index, "is_active", checked)}
                          />
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCircuitCode(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Processing Fee */}
      <Card>
        <CardHeader>
          <CardTitle>Credit Card Processing Fee</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="processing-fee-enabled">Enable Processing Fee</Label>
              <p className="text-sm text-muted-foreground">Add a percentage fee for credit card payments</p>
            </div>
            <Switch
              id="processing-fee-enabled"
              checked={processingFee.enabled}
              onCheckedChange={(checked) => updateProcessingFee("enabled", checked)}
            />
          </div>

          {processingFee.enabled && (
            <>
              <div>
                <Label htmlFor="processing-percentage">Processing Fee Percentage</Label>
                <Input
                  id="processing-percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={processingFee.percentage}
                  onChange={(e) => updateProcessingFee("percentage", parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {processingFee.percentage}% will be added to credit card payments
                </p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm font-medium">Preview:</p>
                <p className="text-sm text-muted-foreground">
                  Customers paying by credit card will see: "+{processingFee.percentage}% processing fee"
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Regular Discount Codes */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Other Discount Codes & Coupons</CardTitle>
            <Button onClick={addDiscount} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Discount Code
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {discountCodes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No discount codes configured. Click "Add Discount Code" to create promotional codes.
            </p>
          ) : (
            discountCodes.map((discount, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label>Discount Code</Label>
                        <Input
                          placeholder="e.g., EARLY25, SUMMER10"
                          value={discount.code}
                          onChange={(e) => updateDiscount(index, "code", e.target.value.toUpperCase())}
                        />
                      </div>
                      <div>
                        <Label>Discount Type</Label>
                        <RadioGroup
                          value={discount.type}
                          onValueChange={(value) => updateDiscount(index, "type", value)}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="percentage" id={`percentage-${index}`} />
                            <Label htmlFor={`percentage-${index}`}>Percentage Off</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="fixed" id={`fixed-${index}`} />
                            <Label htmlFor={`fixed-${index}`}>Fixed Amount Off</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="per_team_volume" id={`per-team-${index}`} />
                            <Label htmlFor={`per-team-${index}`}>Per Team Volume Discount</Label>
                          </div>
                        </RadioGroup>
                        {discount.type === "per_team_volume" && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Applied automatically based on number of teams registered (e.g., 2 teams = discount × 1, 3 teams = discount × 2)
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>
                          {discount.type === "percentage" 
                            ? "Discount Percentage" 
                            : discount.type === "per_team_volume"
                            ? "Discount Per Additional Team (cents)"
                            : "Discount Amount (cents)"}
                        </Label>
                        <Input
                          type="number"
                          placeholder={discount.type === "percentage" ? "25" : "10000"}
                          value={discount.value || ""}
                          onChange={(e) => updateDiscount(index, "value", parseInt(e.target.value) || 0)}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {discount.type === "percentage" 
                            ? `${discount.value}% off` 
                            : discount.type === "per_team_volume"
                            ? `$${((discount.value || 0) / 100).toFixed(2)} off per additional team (2 teams = $${((discount.value || 0) / 100).toFixed(2)}, 3 teams = $${((discount.value || 0) * 2 / 100).toFixed(2)}, etc.)`
                            : `${discount.value || 0} cents off`}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Valid From (optional)</Label>
                          <Input
                            type="date"
                            value={discount.valid_from || ""}
                            onChange={(e) => updateDiscount(index, "valid_from", e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Valid Until (optional)</Label>
                          <Input
                            type="date"
                            value={discount.valid_until || ""}
                            onChange={(e) => updateDiscount(index, "valid_until", e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Usage Limit (optional)</Label>
                        <Input
                          type="number"
                          placeholder="Leave empty for unlimited"
                          value={discount.usage_limit || ""}
                          onChange={(e) => updateDiscount(index, "usage_limit", parseInt(e.target.value) || undefined)}
                        />
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <Label htmlFor={`active-${index}`}>Active</Label>
                        <Switch
                          id={`active-${index}`}
                          checked={discount.is_active}
                          onCheckedChange={(checked) => updateDiscount(index, "is_active", checked)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDiscount(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
